import React from 'react';
import config from './config';
import { POSITIONING } from './compliance/mtdFacts';

const PricingPage = ({ setView }) => {
    return (
        <div className="max-w-5xl mx-auto py-12 px-6">

            <div className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">Simple, Transparent Pricing</h1>
                <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
                    Choose the plan that fits your business. Both include a 30-day free trial with no credit card required.
                </p>
                <p className="text-sm text-slate-500 max-w-2xl mx-auto">
                    {POSITIONING.subHero} {POSITIONING.noSubmit} {POSITIONING.notTaxAdvice}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
                {/* Lite Plan */}
                <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl shadow-xl flex flex-col">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-white mb-2">Lite Plan</h2>
                        <p className="text-slate-400">Budget-friendly tracking for occasional expenses.</p>
                    </div>
                    <div className="mb-8">
                        <span className="text-4xl font-bold text-white">£4.95</span>
                        <span className="text-slate-500 ml-2">/ month</span>
                    </div>
                    <ul className="space-y-4 mb-8 flex-grow">
                        <li className="flex items-start gap-3 text-slate-300">
                            <span className="text-indigo-500 mt-1">✓</span>
                            <span><strong>20 expenses</strong> per month</span>
                        </li>
                        <li className="flex items-start gap-3 text-slate-300">
                            <span className="text-indigo-500 mt-1">✓</span>
                            <span>Full AI extraction features</span>
                        </li>
                    </ul>
                    <div className="pt-6 border-t border-slate-800">
                        <p className="text-sm text-slate-400 italic">Best for: Individuals or freelancers with a low volume of monthly expenses.</p>
                    </div>
                </div>

                {/* Standard Plan */}
                <div className="bg-indigo-600/10 border border-indigo-500/30 p-8 rounded-3xl shadow-xl flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] uppercase font-bold px-3 py-1 rounded-bl-xl tracking-wider">
                        Recommended
                    </div>
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-white mb-2">Standard Plan</h2>
                        <p className="text-indigo-200/60">The full-power solution for busy professionals.</p>
                    </div>
                    <div className="mb-8">
                        <span className="text-4xl font-bold text-white">£6.95</span>
                        <span className="text-slate-500 ml-2">/ month</span>
                    </div>
                    <ul className="space-y-4 mb-8 flex-grow">
                        <li className="flex items-start gap-3 text-slate-100">
                            <span className="text-indigo-400 mt-1">✓</span>
                            <span><strong>300 expenses</strong> per month</span>
                        </li>
                        <li className="flex items-start gap-3 text-slate-100">
                            <span className="text-indigo-400 mt-1">✓</span>
                            <span>High-volume AI extraction</span>
                        </li>
                    </ul>
                    <div className="pt-6 border-t border-indigo-500/20">
                        <p className="text-sm text-indigo-300/60 italic">Best for: Active spenders and frequent travelers who need reliable, high-capacity management.</p>
                    </div>
                </div>
            </div>

            <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-8 md:p-12">
                <h3 className="text-2xl font-bold text-white mb-8 text-center">Shared Plan Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="space-y-3">
                        <div className="text-2xl">📥</div>
                        <h4 className="font-bold text-white text-lg">Multi-Channel Ingest</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Upload receipts via WhatsApp or forward email invoices (PDF/HTML) to your dedicated address.</p>
                    </div>
                    <div className="space-y-3">
                        <div className="text-2xl">🤖</div>
                        <h4 className="font-bold text-white text-lg">Smart AI Extraction</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Automatic extraction of Merchant, Total, VAT, Category, and Date with high accuracy.</p>
                    </div>
                    <div className="space-y-3">
                        <div className="text-2xl">🚗</div>
                        <h4 className="font-bold text-white text-lg">Natural Language Mileage</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Log trips just by describing them. "Drove from LE18 3SP to M18 7BH for a client meeting" is all you need to say.</p>
                    </div>
                    <div className="space-y-3">
                        <div className="text-2xl">📄</div>
                        <h4 className="font-bold text-white text-lg">Structured Monthly Reports</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Generate and receive monthly CSV reports with receipt archives, ready for import into your accounting or filing software.</p>
                    </div>
                    <div className="space-y-3">
                        <div className="text-2xl">🔍</div>
                        <h4 className="font-bold text-white text-lg">Pending Review Queue</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Easy verification interface for any items that require a human double-check.</p>
                    </div>
                    <div className="space-y-3">
                        <div className="text-2xl">🔒</div>
                        <h4 className="font-bold text-white text-lg">Secure &amp; Private</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">Your data is encrypted. {POSITIONING.retention}</p>
                    </div>
                </div>
            </div>

            <div className="mt-16 text-center">
                <a href="#signup" onClick={(e) => { 
                    e.preventDefault(); 
                    setView('home'); 
                    window.history.pushState({}, '', '/#signup');
                    window.location.hash = 'signup'; 
                }} className="inline-block px-8 py-4 bg-white text-slate-950 font-bold rounded-xl hover:bg-slate-200 transition-all">
                    Start Your Free Month
                </a>
                <p className="text-slate-500 text-sm mt-4">No credit card required. Cancel anytime. Support: <a href={`mailto:${config.SUPPORT_EMAIL}`} className="text-indigo-400">{config.SUPPORT_EMAIL}</a></p>
            </div>
        </div>
    );
};

export default PricingPage;
